import axios from 'axios';
import { api } from './api';

export interface CanvasImage {
  id: string;
  data: {
    // Always the 200x200 thumbnail url; it answers 404 when the canvas has no image.
    image: string;
    title: string;
    width: number;
    height: number;
    bg_color: string;
    template_id: number | null;
    template_name: string | null;
    structure: Record<string, unknown>;
    is_active: boolean;
    country_code: string;
    created_at: string;
    modified_at: string;
  };
}

export interface CanvasListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CanvasImage[];
}

export interface CanvasListParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_active?: boolean;
  template_id?: number;
  ordering?: string;
}

export interface CreateCanvasData {
  title: string;
  width: number;
  height: number;
  bg_color?: string;
  template_id?: number | null;
  structure?: Record<string, unknown>;
  is_active?: boolean;
  image?: File | Blob;
}

export type UpdateCanvasData = Partial<CreateCanvasData>;

export interface CanvasBatchStatus {
  status: 'pending' | 'started' | 'success' | 'failed';
  processed: number;
  total: number;
  combosCount: number;
  error: string;
}

// Carries the HTTP status (undefined for network errors) so callers can tell whether retrying makes sense.
export class CanvasServiceError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CanvasServiceError';
    this.status = status;
  }
}

const toServiceError = (error: unknown, fallback: string): CanvasServiceError =>
  new CanvasServiceError(
    getErrorMessage(error, fallback),
    axios.isAxiosError(error) ? error.response?.status : undefined
  );

// keepalive requests (the only kind that survives closing the tab) are capped at 64 KB by browsers.
const KEEPALIVE_MAX_BYTES = 60 * 1024;

// The backend answers validation errors as `{ error: { field: [messages] } }` or `{ error: 'message' }`.
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback;
  const data = error.response?.data as { error?: unknown; detail?: unknown } | undefined;
  const detail = data?.error ?? data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    const messages = Object.values(detail as Record<string, unknown>).flat().filter((m) => typeof m === 'string');
    if (messages.length > 0) return messages.join(' ');
  }
  return error.response ? fallback : 'Error al comunicarse con el servidor';
};

// With form-data the backend reads an empty template_id as "no template".
const toFormData = ({ image, structure, template_id, ...fields }: UpdateCanvasData, emptyTemplate = false): FormData => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) form.append(key, String(value));
  });
  if (template_id !== undefined && template_id !== null) form.append('template_id', String(template_id));
  else if (template_id === null && emptyTemplate) form.append('template_id', '');
  if (structure !== undefined) form.append('structure', JSON.stringify(structure));
  if (image) form.append('image', image, image instanceof File ? image.name : 'canvas.png');
  return form;
};

export const canvasService = {
  getCanvases: async (params?: CanvasListParams): Promise<CanvasListResponse> => {
    try {
      const response = await api.get<CanvasListResponse>('/canvas-images/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener canvas:', error);
      throw new Error(getErrorMessage(error, 'Error al obtener los canvas'));
    }
  },

  getCanvas: async (id: string): Promise<CanvasImage> => {
    try {
      const response = await api.get<CanvasImage>(`/canvas-images/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener canvas:', error);
      throw new Error(getErrorMessage(error, 'Error al obtener el canvas'));
    }
  },

  // Sent as multipart when there's an image, as JSON otherwise.
  createCanvas: async (data: CreateCanvasData): Promise<CanvasImage> => {
    try {
      const response = data.image
        ? await api.post<CanvasImage>('/canvas-image', toFormData(data), {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        : await api.post<CanvasImage>('/canvas-image', data);
      return response.data;
    } catch (error) {
      console.error('Error al crear canvas:', error);
      throw new Error(getErrorMessage(error, 'Error al crear el canvas'));
    }
  },

  // PATCH: only the given fields change. Multipart when there's a new image, JSON otherwise.
  updateCanvas: async (id: string, data: UpdateCanvasData): Promise<CanvasImage> => {
    try {
      const response = data.image
        ? await api.patch<CanvasImage>(`/canvas-images/${id}`, toFormData(data, true), {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        : await api.patch<CanvasImage>(`/canvas-images/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error al guardar canvas:', error);
      throw toServiceError(error, 'Error al guardar el canvas');
    }
  },

  deleteCanvas: async (id: string): Promise<void> => {
    try {
      await api.delete(`/canvas-images/${id}`);
    } catch (error) {
      console.error('Error al eliminar canvas:', error);
      throw new Error(getErrorMessage(error, 'Error al eliminar el canvas'));
    }
  },

  // Fire-and-forget PATCH that the browser completes even if the tab is closing (axios can't do keepalive).
  // Returns false without sending when the payload is too big for a keepalive request.
  updateCanvasOnUnload: (id: string, data: Omit<UpdateCanvasData, 'image'>): boolean => {
    const body = JSON.stringify(data);
    if (new Blob([body]).size > KEEPALIVE_MAX_BYTES) return false;
    const token = localStorage.getItem('access_token');
    void fetch(`${api.defaults.baseURL}/canvas-images/${id}`, {
      method: 'PATCH',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body,
    }).catch(() => undefined);
    return true;
  },

  // The worker creates one combo per row and groups them under Combos creados.
  startCanvasBatch: async (canvasId: string, file: File): Promise<{ taskId: string }> => {
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await api.post(`/canvas-images/${canvasId}/batch`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { taskId: response.data.task_id };
    } catch (error) {
      console.error('Error al generar los combos del canvas:', error);
      throw toServiceError(error, 'No se pudieron generar los combos');
    }
  },

  getCanvasBatchStatus: async (taskId: string): Promise<CanvasBatchStatus> => {
    try {
      const response = await api.get(`/canvas-images/batch/${taskId}/status`);
      return {
        status: response.data.status,
        processed: response.data.processed ?? 0,
        total: response.data.total ?? 0,
        combosCount: response.data.combos_count ?? 0,
        error: response.data.error ?? '',
      };
    } catch (error) {
      console.error('Error al consultar la exportación masiva:', error);
      throw toServiceError(error, 'No se pudo consultar la exportación');
    }
  },

  downloadCanvasBatchFile: async (taskId: string, kind: 'zip' | 'urls'): Promise<Blob> => {
    try {
      const response = await api.get(`/canvas-images/batch/${taskId}/download`, {
        params: { file: kind },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error al descargar los combos:', error);
      throw toServiceError(error, 'No se pudo descargar el archivo');
    }
  },
};
